import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useFetch from '../../../../hooks/useFetch';
import appConfig from '../../../../config/app-config';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const API_KEY = COMMUNICATION_ENV.key;
const BASE_URL = COMMUNICATION_ENV.address;

export type ContactDetail = {
  id: string;
  type: 'PF' | 'PJ';
  document: string;
  rootDocument?: string;
  image?: string | null;
  name: string;
  website?: string;
  city?: string;
  state?: string;
  observation: string;
  status: string;
  farol?: string;
  createdAt?: string;
  nickname?: string;
  birthDate?: string;
  jobTitle?: string;
  socialMedia?: {
    website?: string;
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
  };
  tags: Array<{ id: string; label: string; color: string; canEditColor?: boolean; canRemove?: boolean }>;
  phones: Array<{ id: string; number: string; active: boolean; primary: boolean; observation: string; template: string; whatsapp: boolean }>;
  emails: Array<{ id: string; address: string; active: boolean; primary: boolean; observation: string; template: string }>;
  history: Array<{ id: string; user: string; action: string; timestamp: string }>;
  interactions?: Array<{ id: string; type: string; description: string; timestamp: string; canRemove?: boolean }>;
  clientInfo?: {
    segment: string;
    size: string;
    employees: string;
    revenue: string;
    since: string;
  };
  companies?: Array<{ id: string; name: string; document: string; role: string }>;
  people?: Array<{ id: string; name: string; document: string; role: string }>;
  branches?: Array<{ id: string; name: string; document: string; city: string; state: string; role?: string }>;
  deals?: Array<{ id: string; operation: string; number: string; assignor: string; assignorTaxId?: string; value: string; valueFormatted?: string; amountPaid?: string; amountPaidFormatted?: string; status: string; dueDate: string }>;
  isHeadquarters?: boolean;
};

export const TAG_COLORS = [
  { value: '#8B5CF6', label: 'Roxo' },
  { value: '#22C55E', label: 'Verde' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#EAB308', label: 'Amarelo' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#6366F1', label: 'Índigo' },
  { value: '#F97316', label: 'Laranja' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#78716C', label: 'Cinza' },
  { value: '#0EA5E9', label: 'Celeste' },
  { value: '#A855F7', label: 'Violeta' },
];

function mapApiToContact(apiData: any): ContactDetail {
  return {
    id: apiData.id,
    type: apiData.contactType === 'INDIVIDUAL' ? 'PF' : 'PJ',
    document: apiData.taxIdFormatted || apiData.taxId,
    rootDocument: apiData.taxRootId,
    name: apiData.fullName,
    observation: apiData.notes || '',
    status: apiData.statusFormatted,
    farol: apiData.status,
    image: apiData.avatarUrl || null,
    createdAt: apiData.createdAt,
    city: apiData.profile?.city || apiData.city,
    state: apiData.profile?.state || apiData.state,
    socialMedia: apiData.profile?.socialMedia || apiData.socialMedia,
    website: apiData.profile?.socialMedia?.website || apiData.socialMedia?.website,
    nickname: apiData.profile?.nickname,
    birthDate: apiData.profile?.birthDate,
    jobTitle: apiData.profile?.jobTitle,
    tags: (apiData.tags || []).map((tag: any) => ({
      id: tag.id || tag.label,
      label: tag.label,
      color: tag.color || '#808080',
      canEditColor: tag.canEditColor || false,
      canRemove: tag.canRemove || false,
    })),
    phones: apiData.contactPoints
      ?.filter((cp: any) => cp.type === 'PHONE' || cp.type === 'WHATSAPP')
      .map((cp: any) => ({
        id: cp.id,
        number: cp.valueFormatted,
        active: cp.isActive,
        primary: cp.isPrimary || false,
        observation: cp.notes || '',
        template: cp.templates?.join(', ') || '',
        whatsapp: cp.isWhatsapp || cp.type === 'WHATSAPP'
      })) || [],
    emails: apiData.contactPoints
      ?.filter((cp: any) => cp.type === 'EMAIL')
      .map((cp: any) => ({
        id: cp.id,
        address: cp.valueFormatted,
        active: cp.isActive,
        primary: cp.isPrimary || false,
        observation: cp.notes || '',
        template: cp.templates?.join(', ') || ''
      })) || [],
    history: (apiData.history || []).map((h: any) => ({
      id: h.id || `history-${Date.now()}`,
      user: h.userName,
      action: h.action,
      timestamp: h.occurredAtFormatted
    })),
    interactions: (apiData.interactions || []).map((int: any) => ({
      id: int.id,
      type: int.interactionType,
      description: int.body || int.title,
      timestamp: int.occurredAt,
      canRemove: int.canRemove || false,
    })),
    companies: apiData.companies || [],
    people: apiData.people || [],
    branches: apiData.branches || [],
    deals: apiData.deals || [],
    isHeadquarters: apiData.profile?.isHeadquarters || false,
    clientInfo: apiData.contactType === 'LEGAL_PERSON' ? {
      segment: apiData.profile?.segment || '',
      size: apiData.profile?.size || '',
      employees: apiData.profile?.employees || '',
      revenue: apiData.profile?.revenue || '',
      since: ''
    } : undefined
  };
}

export function getFarolColor(farol: string) {
  switch (farol) {
    case 'FAST': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
    case 'WAITING': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
    case 'LONG_TIME': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
    case 'MODERATELY_FAST': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
    case 'NO_RESPOND': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
    default: return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
  }
}

export function formatLongDate(timestamp: string) {
  return new Date(timestamp).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function formatActivityDate(timestamp: string) {
  const date = new Date(timestamp);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('pt-BR', { month: 'long' });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${day} de ${month} de ${year} às ${time}`;
}

export function getMonthYear(timestamp: string) {
  return new Date(timestamp).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

export function getCommunicationIcon(type: string) {
  return type;
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

export default function useContactPageView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const url = `${BASE_URL}/contacts/${id}/page-view`;
  const fetchHeaders = [{ key: 'x-api-key', value: API_KEY || '' }];
  const { data: apiData, isLoading, error } = useFetch(url, fetchHeaders);

  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [newTag, setNewTag] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [customColor, setCustomColor] = useState('#808080');
  const [activeTab, setActiveTab] = useState(0);

  const tabItems = [
    { id: 'about', title: 'Sobre este contato' },
    { id: 'activity', title: 'Atividades' },
    { id: 'overview', title: 'Visão geral' },
    ...(contact?.type === 'PF' && contact?.companies ? [{ id: 'companies', title: 'Empresas', badge: contact.companies.length || undefined }] : []),
    ...(contact?.type === 'PJ' && contact?.people ? [{ id: 'people', title: 'Pessoas', badge: contact.people.length || undefined }] : []),
    ...(contact?.type === 'PJ' && contact?.branches?.length ? [{ id: 'branches', title: contact.isHeadquarters ? 'Filiais' : 'Empresas do Grupo', badge: contact.branches.length || undefined }] : []),
    ...(contact?.deals?.length ? [{ id: 'deals', title: 'Duplicatas', badge: contact.deals.length || undefined }] : []),
  ].map((item, idx) => ({ ...item, index: idx }));
  const activeTabId = tabItems[activeTab]?.id || 'about';
  const [showCallModal, setShowCallModal] = useState(false);
  const [interactionToRemove, setInteractionToRemove] = useState<string | null>(null);
  const [removingInteraction, setRemovingInteraction] = useState(false);
  const [loadingContactPoint, setLoadingContactPoint] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteLabel, setConfirmDeleteLabel] = useState('');
  const [deletingContactPoint, setDeletingContactPoint] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoadingContactPoint(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  useEffect(() => {
    if (apiData) {
      setContact(mapApiToContact(apiData));
    }
  }, [apiData]);

  const handleDeleteContactPoint = async (contactPointId: string) => {
    if (deletingContactPoint) return;
    setDeletingContactPoint(true);
    try {
      const session = await (await import('aws-amplify')).Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const response = await fetch(`${BASE_URL}/contacts/${id}/contact-points/${contactPointId}`, {
        method: 'DELETE',
        headers: { 'x-api-key': API_KEY || '', 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setConfirmDeleteId(null);
        setContact((prev) => {
          if (!prev) return prev;
          const newHistory = result.historyEntry
            ? [{ id: `history-${Date.now()}`, user: result.historyEntry.userName, action: result.historyEntry.action, timestamp: result.historyEntry.occurredAtFormatted }, ...prev.history].slice(0, 10)
            : prev.history;
          return {
            ...prev,
            phones: prev.phones.filter((p) => p.id !== contactPointId),
            emails: prev.emails.filter((e) => e.id !== contactPointId),
            history: newHistory,
          };
        });
      }
    } catch (err) {
      console.error('Erro ao remover contact point:', err);
    } finally {
      setDeletingContactPoint(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && contact) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setContact({ ...contact, image: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddTag = async () => {
    if (!contact || !id || addingTag) return;
    const tagLabel = newTag.trim().toUpperCase().replace(/\s/g, '');
    if (!tagLabel) return;
    if (contact.tags.some(tag => tag.label === tagLabel)) {
      toast.error('Contato já possui esta tag');
      return;
    }
    setAddingTag(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/contacts/${id}/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY || '', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ name: tagLabel }),
      });
      if (response.ok) {
        const data = await response.json();
        setContact({ ...contact, tags: [...contact.tags, { id: data.id || data.name, label: data.name, color: data.color, canEditColor: true, canRemove: true }] });
        setNewTag('');
        toast.success('Tag adicionada');
      } else {
        const err = await response.json();
        toast.error(err.message || 'Erro ao adicionar tag');
      }
    } catch { toast.error('Erro ao adicionar tag'); } finally { setAddingTag(false); }
  };

  const handleRemoveTag = async (tagName: string) => {
    if (!contact || !id) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/contacts/${id}/tags/${encodeURIComponent(tagName)}`, {
        method: 'DELETE',
        headers: { 'x-api-key': API_KEY || '', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      if (response.ok) {
        setContact({ ...contact, tags: contact.tags.filter(tag => tag.label !== tagName) });
        toast.success('Tag removida');
      } else {
        const err = await response.json();
        toast.error(err.message || 'Erro ao remover tag');
      }
    } catch { toast.error('Erro ao remover tag'); }
  };

  const handleTagClick = (tagId: string) => {
    setEditingTagId(editingTagId === tagId ? null : tagId);
  };

  const handleColorChange = async (color: string) => {
    if (!editingTagId || !contact) return;
    const tag = contact.tags.find(t => t.id === editingTagId);
    if (!tag) return;
    // We need the tag's real ID from the API - for now use name to find
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/tags/${tag.id}/color`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY || '', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ color }),
      });
      if (response.ok) {
        setContact({ ...contact, tags: contact.tags.map(t => t.id === editingTagId ? { ...t, color } : t) });
        setEditingTagId(null);
        toast.success('Cor atualizada');
      } else {
        const err = await response.json();
        toast.error(err.message || 'Erro ao atualizar cor');
      }
    } catch { toast.error('Erro ao atualizar cor'); }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAddTag();
  };

  const handleRegisterCall = (newActivity: any) => {
    if (!contact) return;
    const updates: any = {
      interactions: [{
        id: newActivity.interactionId,
        type: newActivity.interactionType,
        description: `${newActivity.title}${newActivity.body ? ' - ' + newActivity.body : ''}`,
        timestamp: newActivity.occurredAt,
        canRemove: true,
      }, ...(contact.interactions || [])]
    };
    if (newActivity.historyEntry) {
      updates.history = [{
        id: `history-${Date.now()}`,
        user: newActivity.historyEntry.userName,
        action: newActivity.historyEntry.action,
        timestamp: newActivity.historyEntry.occurredAtFormatted
      }, ...(contact.history || [])].slice(0, 10);
    }
    setContact({ ...contact, ...updates });
    setActiveTab(1);
  };

  const handleRemoveInteraction = async () => {
    if (!contact || !interactionToRemove) return;
    setRemovingInteraction(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/contacts/${contact.id}/interactions/${interactionToRemove}`, {
        method: 'DELETE',
        headers: {
          'x-api-key': API_KEY || '',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (response.ok) {
        setContact({ ...contact, interactions: (contact.interactions || []).filter(i => i.id !== interactionToRemove) });
        toast.success('Atividade removida com sucesso!');
      } else {
        const err = await response.json();
        toast.error(err.message || 'Erro ao remover atividade');
      }
    } catch {
      toast.error('Erro ao remover atividade');
    } finally {
      setRemovingInteraction(false);
      setInteractionToRemove(null);
    }
  };

  const onContactUpdate = (updatedContact: any) => {
    const updates: any = {
      ...contact!,
      name: updatedContact.fullName || contact!.name,
      observation: updatedContact.notes ?? contact!.observation,
      farol: updatedContact.status || contact!.farol,
      status: updatedContact.statusFormatted || contact!.status,
    };
    if (updatedContact.profile?.individual) {
      updates.nickname = updatedContact.profile.individual.nickname;
      updates.birthDate = updatedContact.profile.individual.birthDate;
      updates.jobTitle = updatedContact.profile.individual.jobTitle;
    }
    if (updatedContact.profile?.address) {
      updates.city = updatedContact.profile.address.city;
      updates.state = updatedContact.profile.address.state;
    }
    if (updatedContact.profile?.socialMedia) {
      updates.socialMedia = updatedContact.profile.socialMedia;
      updates.website = updatedContact.profile.socialMedia.website;
    }
    if (updatedContact.profile?.legalPerson) {
      updates.clientInfo = {
        ...(contact!.clientInfo || {}),
        segment: updatedContact.profile.legalPerson.segment || '',
        size: updatedContact.profile.legalPerson.size || '',
        employees: updatedContact.profile.legalPerson.employees || '',
        revenue: updatedContact.profile.legalPerson.revenue || '',
        isHeadquarters: updatedContact.profile.legalPerson.isHeadquarters || false,
      };
    }
    if (updatedContact.historyEntry) {
      updates.history = [{
        id: `history-${Date.now()}`,
        user: updatedContact.historyEntry.userName,
        action: updatedContact.historyEntry.action,
        timestamp: updatedContact.historyEntry.occurredAtFormatted,
      }, ...(contact!.history || [])].slice(0, 10);
    }
    setContact(updates);
  };

  const onContactPointUpdate = (updated: any) => {
    if (!contact) return;
    const updates: any = { ...contact };

    if (updated.type === 'PHONE' || updated.type === 'WHATSAPP') {
      const existing = contact.phones.find(p => p.id === updated.id);
      const mapped = {
        id: updated.id,
        number: updated.value,
        active: updated.isActive,
        primary: updated.isPrimary,
        whatsapp: updated.isWhatsapp || updated.type === 'WHATSAPP',
        observation: updated.notes || '',
        template: updated.templates?.join(', ') || ''
      };
      updates.phones = existing
        ? contact.phones.map(p => p.id === updated.id ? mapped : p)
        : [mapped, ...contact.phones];
    } else if (updated.type === 'EMAIL') {
      const existing = contact.emails.find(e => e.id === updated.id);
      const mapped = {
        id: updated.id,
        address: updated.value,
        active: updated.isActive,
        primary: updated.isPrimary,
        observation: updated.notes || '',
        template: updated.templates?.join(', ') || ''
      };
      updates.emails = existing
        ? contact.emails.map(e => e.id === updated.id ? mapped : e)
        : [mapped, ...contact.emails];
    }

    if (updated.historyEntry) {
      updates.history = [{
        id: `history-${Date.now()}`,
        user: updated.historyEntry.userName,
        action: updated.historyEntry.action,
        timestamp: updated.historyEntry.occurredAtFormatted
      }, ...(contact.history || [])].slice(0, 10);
    }
    setContact(updates);
  };

  return {
    navigate, id, contact, isLoading, error, fileInputRef,
    // Tab
    activeTab, setActiveTab, tabItems, activeTabId,
    // Tags
    newTag, setNewTag, addingTag, customColor, setCustomColor, editingTagId,
    handleAddTag, handleRemoveTag, handleTagClick, handleColorChange, handleKeyPress,
    // Modals
    showCallModal, setShowCallModal,
    interactionToRemove, setInteractionToRemove, removingInteraction,
    // Contact point
    openMenuId, setOpenMenuId, loadingContactPoint, setLoadingContactPoint,
    confirmDeleteId, setConfirmDeleteId, confirmDeleteLabel, setConfirmDeleteLabel,
    deletingContactPoint,
    // Handlers
    handleImageUpload, handleDeleteContactPoint,
    handleRegisterCall, handleRemoveInteraction,
    onContactUpdate, onContactPointUpdate,
  };
}
